import { useOnboardingTrace } from '@/shared/hooks/queries/use-onboarding-trace'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { useUser } from '@/shared/hooks/use-user'
import { useState, useCallback, useEffect, useRef } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ROUTES } from '@/shared/constants/routes'
import { sendEvent } from '@/shared/hooks/use-analytics'
import { PRODUCT } from '@/shared/constants/product'
import {
  ONBOARDING_MESSAGE,
  ONBOARDING_STATUS,
} from '@/shared/constants/onboarding'
import { useLanguage } from '@/shared/lib/i18n'
import { LANGUAGE } from '@/shared/constants/language'
import { postMessageToParent } from '@/shared/helpers/post-message'
import { useFeatureFlagVariantKey } from 'posthog-js/react'
import { FEATURE_FLAGS } from '@/shared/constants/feature-flags'

const HIDE_ONBOARDING_FORM_TEST_VARIANT = 'test'

const COMPLETED_EVENT_NAMES = [
  'ob_pet_screen_one_completed',
  'ob_pet_screen_two_completed',
  'ob_pet_screen_three_completed',
  'ob_pet_screen_four_completed',
  'ob_pet_screen_five_completed',
  'ob_pet_screen_six_completed',
] as const

export function useOnboardingPetPage() {
  const navigate = useNavigate()
  const { currentLanguage } = useLanguage()

  const { user } = useUser()
  const userId = user?.id

  const { data: pets, isLoading: isPetsLoading } = usePets()
  const { data: policies, isLoading: isPoliciesLoading } = usePolicies()
  const {
    data: onboardingTrace,
    isFetched: isOnboardingTracesFetched,
    createMutation,
    updateMutation,
  } = useOnboardingTrace({ product_type: PRODUCT.PET }, !!userId)

  const onboardingPolicy = policies?.[0]
  const onboardingPet =
    onboardingPolicy && pets?.find((pet) => pet.id === onboardingPolicy?.petId)

  const [isModalOpen, setIsModalOpen] = useState(true)
  const [currentStep, setCurrentStep] = useState(0)
  const [isCompleting, setIsCompleting] = useState(false)
  const isOnboardingInitialized = useRef(false)
  const hideOnboardingFormVariant = useFeatureFlagVariantKey(
    FEATURE_FLAGS.HIDE_ONBOARDING_FORM,
  )
  const hideOnboardingForm =
    hideOnboardingFormVariant === HIDE_ONBOARDING_FORM_TEST_VARIANT

  useEffect(() => {
    postMessageToParent(ONBOARDING_MESSAGE.SHOW_ONBOARDING_BUTTON_CLOSE)
  }, [])

  useEffect(() => {
    if (
      !isOnboardingTracesFetched ||
      !userId ||
      isOnboardingInitialized.current
    )
      return
    isOnboardingInitialized.current = true

    if (!onboardingTrace) {
      sendEvent('ob_pet_started')

      createMutation.mutate(PRODUCT.PET)
      return
    }

    setCurrentStep(onboardingTrace.step - 1)
  }, [isOnboardingTracesFetched, userId, onboardingTrace])

  const closeModal = useCallback(() => {
    postMessageToParent(ONBOARDING_MESSAGE.COMPLETE_ONBOARDING)
    navigate({ to: ROUTES.HOME })
    setIsModalOpen(false)
  }, [navigate])

  const goToNextStep = useCallback(() => {
    if (currentStep >= COMPLETED_EVENT_NAMES.length - 1) return
    const next = currentStep + 1
    const event = COMPLETED_EVENT_NAMES[currentStep]
    if (event) {
      sendEvent(event)
    }
    if (onboardingTrace) {
      updateMutation.mutate({
        id: onboardingTrace.id,
        body: { step: next + 1 },
      })
    }
    setCurrentStep(next)
  }, [currentStep, onboardingTrace, updateMutation])

  const goToPreviousStep = useCallback(() => {
    const prev = Math.max(0, currentStep - 1)
    if (onboardingTrace) {
      updateMutation.mutate({
        id: onboardingTrace.id,
        body: { step: prev + 1 },
      })
    }
    setCurrentStep(prev)
  }, [currentStep, onboardingTrace, updateMutation])

  const completeOnboarding = useCallback(() => {
    setIsCompleting(true)

    if (onboardingTrace) {
      sendEvent('ob_pet_screen_six_completed')
      sendEvent('ob_pet_completed')
      updateMutation.mutate(
        {
          id: onboardingTrace.id,
          body: { status: ONBOARDING_STATUS.COMPLETED },
        },
        {
          onSuccess: () => {
            if (hideOnboardingForm && onboardingPolicy?.isFreeLook) {
              navigate({
                to: ROUTES.HOME,
              })
            } else {
              navigate({
                to: ROUTES.ONBOARDING_PET_FORM,
              })
            }
          },
          onError: () => setIsCompleting(false),
        },
      )
    } else {
      navigate({
        to: ROUTES.ONBOARDING_PET_FORM,
      })
    }
  }, [
    onboardingTrace,
    updateMutation,
    navigate,
    hideOnboardingForm,
    onboardingPolicy,
  ])

  const isPolicyDataLoading = isPetsLoading || isPoliciesLoading

  return {
    isModalOpen,
    closeModal,
    currentStep,
    isPolicyDataLoading,
    isCompleting,
    goToNextStep,
    goToPreviousStep,
    completeOnboarding,
    onboardingPolicy,
    onboardingPet,
    user,
    isEnglish: currentLanguage === LANGUAGE.EN,
  }
}
